import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/context/i18n";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, Briefcase, Hotel, Plane, Ticket, Car } from "lucide-react";

const ICON = { hotel: Hotel, flight: Plane, activity: Ticket, transfer: Car };

export default function MyTrips() {
  const { user, formatPrice, authLoading } = useApp();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      sessionStorage.setItem("postLoginRedirect", "/trips");
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    api.get("/bookings").then((r) => setBookings(r.data)).finally(() => setLoading(false));
  }, [user, authLoading]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="pt-24 max-w-4xl mx-auto px-4 lg:px-8 min-h-[60vh]">
        <span className="label-caps">{t("itinerary")}</span>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-[#0B2038] mb-6 mt-1">{t("trips")}</h1>
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#009E9E]" /></div>
        ) : bookings.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-center">
            <Briefcase className="w-10 h-10 text-[#475569] mx-auto mb-3" strokeWidth={1.4} />
            <p className="text-[#475569] mb-4">{t("no_bookings")}</p>
            <Button onClick={() => navigate("/")} className="rounded-full bg-[#009E9E] hover:bg-[#007A7A] text-white font-semibold">{t("start_exploring")}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b) => {
              const Icon = ICON[b.productType] || Briefcase;
              return (
                <Link key={b.reference} to={`/confirmation/${b.reference}`} state={{ booking: b }} data-testid={`trip-${b.reference}`}
                  className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden grid grid-cols-12 transition-transform duration-300 hover:-translate-y-1">
                  <div className="col-span-3 sm:col-span-2 h-full min-h-[96px] bg-[#E2E8F0]">
                    {b.image ? <img src={b.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Icon className="w-6 h-6 text-[#475569]" /></div>}
                  </div>
                  <div className="col-span-6 sm:col-span-7 p-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#009E9E] flex items-center gap-1"><Icon className="w-3.5 h-3.5" />{b.productType}</span>
                    <p className="font-display text-lg font-bold text-[#0B2038] leading-tight mt-0.5">{b.title}</p>
                    <p className="text-xs text-[#475569] mt-0.5">{b.subtitle}</p>
                  </div>
                  <div className="col-span-3 p-4 flex flex-col justify-center items-end border-l border-[#E2E8F0]">
                    <p className="font-display text-lg font-bold text-[#0B2038]">{formatPrice(b.priceEur)}</p>
                    <span className="text-xs text-green-600 font-semibold">{b.status}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
